// Radial Brand Atlas — ported from the reference design.
//
// The reference is in /tmp/graph-vis/{radial-layout.js,radial-graph.jsx,data.js}.
// We mirror the visual structure (sector angles, OKLCH category colors,
// per-category placement strategies, decorative rings, brand core),
// but adapt to our real data shape: GraphSnapshot from the server.
//
// Phase 3.5a: Atlas structure + sectors + node placement + status card.
// Tensions overlay (3.5b), live spotlight (3.5c), and zoom-into-category
// (later) land in subsequent slices.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api } from "@/lib/api";
import type { GraphNode, GraphSnapshot, GraphTension } from "@/lib/types";
import { cn } from "@/lib/utils";

interface BrandTenant {
  tenant_id: string;
  name: string | null;
  created_at: string | null;
  updated_at: string | null;
  node_count: number;
}

// ─── Category mapping — DATA-DRIVEN, not fixed by design ──────────────
//
// The reference mockup shows 7 categories at hand-picked angles, but
// in reality:
//   - A brand may have empty sectors (no rituals yet)
//   - Tier-2 categories arrive via propose_new_node_type and could be
//     anything (ContentPillar, Channel, Persona, ...)
//   - Categories can be renamed or retired
//
// So the layout is: define a canonical ORDER for the known categories
// (Tier-1 atlas slots), append unknown Tier-2 server categories at the
// end alphabetically, then compute angles dynamically as 360°/N evenly
// spaced starting from the top. The visual stays stable for a given
// brand and rearranges gracefully when new categories arrive.

interface AtlasCategory {
  /** Stable atlas id (used for color, layout strategy lookup) */
  id: string;
  /** Display label */
  label: string;
  /** OKLCH hue used for the sector tint + bullets */
  hue: number;
  /** Server categories that fold into this atlas slot. For Tier-2
   * categories the serverCats list is just [type_name]. */
  serverCats: string[];
  /** When true, the sector renders even with 0 nodes (Tier-1
   * categories are always visible as part of the canonical brand
   * shape, even before any extraction has happened). */
  alwaysShow: boolean;
  /** Per-category placement strategy. `radial-stack` is for categories
   * with long labels (photography descriptions), so labels stack along
   * a radial line rather than wrapping around an arc. `zigzag` works
   * for short labels. */
  layout: "zigzag" | "radial-stack";
}

// Canonical Tier-1 slots, in the order shown around the wheel
// (starting at the top, going clockwise). Order matters for visual
// stability but the actual angles are computed dynamically.
const TIER1_CATEGORIES: AtlasCategory[] = [
  { id: "voice", label: "Voice", hue: 28, serverCats: ["ToneOfVoice"], alwaysShow: true, layout: "zigzag" },
  { id: "values", label: "Values", hue: 260, serverCats: ["Value", "RejectedValue"], alwaysShow: true, layout: "zigzag" },
  { id: "audience", label: "Audience", hue: 200, serverCats: ["Audience"], alwaysShow: true, layout: "zigzag" },
  { id: "photography", label: "Photography", hue: 140, serverCats: ["PhotographyStyle"], alwaysShow: true, layout: "radial-stack" },
  { id: "palette", label: "Palette", hue: 0, serverCats: ["Color", "ColorPalette"], alwaysShow: true, layout: "zigzag" },
  { id: "notes", label: "Notes", hue: 50, serverCats: ["Note"], alwaysShow: true, layout: "zigzag" },
  { id: "rituals", label: "Rituals", hue: 310, serverCats: ["Ritual"], alwaysShow: false, layout: "zigzag" },
];

const TIER1_SERVER_NAMES = new Set(
  TIER1_CATEGORIES.flatMap((c) => c.serverCats)
);

// OKLCH hue assignments for Tier-2 categories the user adds at runtime.
// We pick from this pool in order so distinct Tier-2 types render with
// distinct colors. (A simple hash → hue would also work; the pool is
// nice because it picks aesthetic colors not just any-old-hue.)
const TIER2_HUE_POOL = [310, 80, 220, 340, 180, 290, 100, 250, 360, 60];

/** Build the live category list for the current snapshot. Always
 *  includes alwaysShow Tier-1 slots; appends Tier-2 categories that
 *  have nodes. Tier-2 ordering is alphabetical → stable per brand. */
function buildLiveCategories(snapshot: GraphSnapshot | null): AtlasCategory[] {
  const live: AtlasCategory[] = [];
  const nodesByCat = snapshot?.nodes_by_category ?? {};

  // Tier-1 in canonical order; include if always-show OR has nodes
  for (const cat of TIER1_CATEGORIES) {
    const hasNodes = cat.serverCats.some((s) => (nodesByCat[s]?.length ?? 0) > 0);
    if (cat.alwaysShow || hasNodes) {
      live.push(cat);
    }
  }

  // Tier-2: anything in nodesByCat not covered by a Tier-1 slot
  const unknown: string[] = [];
  for (const serverName of Object.keys(nodesByCat)) {
    if (!TIER1_SERVER_NAMES.has(serverName)) {
      if ((nodesByCat[serverName]?.length ?? 0) > 0) {
        unknown.push(serverName);
      }
    }
  }
  unknown.sort(); // alphabetical → stable order across reloads
  unknown.forEach((serverName, i) => {
    live.push({
      id: `tier2:${serverName}`,
      label: serverName,
      hue: TIER2_HUE_POOL[i % TIER2_HUE_POOL.length],
      serverCats: [serverName],
      alwaysShow: false,
      layout: "zigzag",
    });
  });

  return live;
}

/** Distribute live categories evenly around the wheel: 360°/N spacing,
 *  starting at -90° (top), going clockwise. */
function computeAngles(live: AtlasCategory[]): Map<string, number> {
  const out = new Map<string, number>();
  const n = Math.max(live.length, 1);
  const step = 360 / n;
  live.forEach((c, i) => {
    out.set(c.id, -90 + i * step);
  });
  return out;
}

// Layout constants — match the reference design exactly so the
// proportions feel right out of the gate.
const R_CORE = 70;
const R_CAT_LABEL = 130;
const R_LEAF_BASE = 250;
const R_OUTER_GUIDE = 420;

// ─── Helpers ──────────────────────────────────────────────────────────

function oklch(lightness: number, chroma: number, hue: number, alpha = 1): string {
  return `oklch(${lightness} ${chroma} ${hue} / ${alpha})`;
}

function readableOn(hex: string): string {
  const h = hex.replace("#", "");
  if (h.length < 6) return "#1F2937";
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum > 145 ? "#1A1815" : "#FFFFFF";
}

interface AtlasNode {
  id: string;
  label: string;
  catId: string; // atlas category id
  hex: string | null; // for palette nodes
  conf: number; // 0..1
  /** Set true when this node is part of one or more live tensions —
   * controls the dashed-red border + warning icon styling. */
  tension: boolean;
  /** Structural parent of this node (a non-Brand ancestor). Null when
   *  the node hangs directly off Brand. Used for parent→child curves
   *  in the atlas. */
  parentId: string | null;
}

/** Map server snapshot → atlas node list, using the live category list
 *  to resolve server categories (including Tier-2). The `tensionSet` is
 *  every node id involved in a live tension — used to flag those nodes
 *  for the dashed-red treatment. */
function mapSnapshotToAtlas(
  snapshot: GraphSnapshot | null,
  live: AtlasCategory[],
  tensionSet: Set<string>
): AtlasNode[] {
  if (!snapshot) return [];
  const byServer: Record<string, AtlasCategory> = {};
  for (const c of live) for (const s of c.serverCats) byServer[s] = c;

  // Defensive dedup: if the snapshot contains the same node id under
  // more than one (server-cat, edge) row, only the first wins. Older
  // backends could return one row per inbound edge — keeping our chip
  // count + child counts honest.
  const seenIds = new Set<string>();
  const out: AtlasNode[] = [];
  for (const [serverCat, nodes] of Object.entries(snapshot.nodes_by_category ?? {})) {
    const cat = byServer[serverCat];
    if (!cat) continue;
    for (const n of nodes as GraphNode[]) {
      if (seenIds.has(n.id)) continue;
      seenIds.add(n.id);
      const hex =
        cat.id === "palette" && typeof n.extra?.hex === "string"
          ? (n.extra.hex as string)
          : null;
      const conf =
        typeof n.provenance?.source_confidence === "number"
          ? (n.provenance.source_confidence as number)
          : 1;
      out.push({
        id: n.id,
        label: n.display_label || n.content || n.id,
        catId: cat.id,
        hex: hex && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : null,
        conf,
        tension: tensionSet.has(n.id),
        // Tree shape: parent_id is set when the node hangs off another
        // category node (e.g. a "rooftop golden hour" anchor parented
        // under "Outdoor" branch under PhotographyStyle). Null = direct
        // child of Brand (rendered at the sector's leaf ring as today).
        parentId:
          typeof (n as GraphNode & { parent_id?: string | null }).parent_id ===
          "string"
            ? ((n as GraphNode & { parent_id?: string | null }).parent_id as string)
            : null,
      });
    }
  }
  return out;
}

/** Compute positions for every node using the per-category placement
 *  strategy + the dynamic angle map.
 *
 *  Depth-aware: root nodes (parentId === null) are placed first using
 *  the per-category zigzag/radial-stack strategy. Then children are
 *  positioned in a sub-fan radially OUTWARD from their parent. Without
 *  this depth-awareness, all category nodes would pile up at the same
 *  ring regardless of parent — making sub-trees visually invisible.
 */
function computeOverview(
  nodes: AtlasNode[],
  live: AtlasCategory[],
  angles: Map<string, number>
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();

  // ─── Pass 1: position root nodes (parentId === null) per category ──
  // Layout scales with node count — crowded sectors get a wider
  // angular spread + bigger radial step so chips don't pile up.
  for (const cat of live) {
    const angle = angles.get(cat.id);
    if (angle === undefined) continue;
    const ns = nodes.filter((n) => n.catId === cat.id && !n.parentId);
    if (ns.length === 0) continue;
    const baseA = (angle * Math.PI) / 180;
    ns.forEach((n, i) => {
      let a: number, r: number;
      if (ns.length === 1) {
        a = baseA;
        r = R_LEAF_BASE;
      } else if (cat.layout === "radial-stack") {
        // Long-label categories. Spread + step grow with count.
        const t = i / (ns.length - 1) - 0.5;
        const angularSpread = ns.length > 4 ? 36 : 12;
        const radialStep = ns.length > 4 ? 58 : 42;
        a = ((angle + t * angularSpread) * Math.PI) / 180;
        r = R_LEAF_BASE - 60 + i * radialStep;
      } else {
        // zigzag — short-label categories. Use 3 radius bands once
        // there are >5 nodes so chips don't pile on two columns.
        const t = i / (ns.length - 1) - 0.5;
        const spread =
          ns.length > 5 ? 64 : ns.length > 3 ? 42 : 22;
        a = ((angle + t * spread) * Math.PI) / 180;
        if (ns.length > 5) {
          const band = i % 3;
          r = R_LEAF_BASE + (band === 0 ? -42 : band === 1 ? 0 : 42);
        } else {
          r = R_LEAF_BASE + (i % 2 === 0 ? -35 : 35);
        }
      }
      positions.set(n.id, { x: r * Math.cos(a), y: r * Math.sin(a) });
    });
  }

  // ─── Pass 2: place children in a sub-fan around each parent ─────────
  // Build a parent→siblings map so each child knows its slot index.
  const siblingsByParent = new Map<string, AtlasNode[]>();
  for (const n of nodes) {
    if (n.parentId) {
      const arr = siblingsByParent.get(n.parentId) ?? [];
      arr.push(n);
      siblingsByParent.set(n.parentId, arr);
    }
  }

  // Walk the tree in topological order — a child can only be placed
  // after its parent is in `positions`. Re-queue children whose parent
  // isn't ready yet (handles 3-level trees: grandparent → parent →
  // grandchild).
  const queue: AtlasNode[] = nodes.filter((n) => n.parentId);
  let attemptsLeft = queue.length * 3 + 1;
  while (queue.length > 0 && attemptsLeft > 0) {
    attemptsLeft--;
    const node = queue.shift();
    if (!node || !node.parentId) continue;
    const parentPos = positions.get(node.parentId);
    if (!parentPos) {
      // Parent not positioned yet — try again later.
      queue.push(node);
      continue;
    }
    const siblings = siblingsByParent.get(node.parentId) ?? [];
    const idx = siblings.findIndex((s) => s.id === node.id);
    const sibCount = siblings.length;
    // Parent's angle from origin (north pole = -π/2)
    const parentAngle = Math.atan2(parentPos.y, parentPos.x);
    // Sub-arc spread for children — grows with sibling count up to a cap
    const subArcDeg = Math.min(18 + sibCount * 6, 56);
    const t = sibCount === 1 ? 0 : idx / (sibCount - 1) - 0.5;
    const childAngle = parentAngle + (t * subArcDeg * Math.PI) / 180;
    // Radial distance: push children outward 80px from the parent so
    // they don't collide with the parent chip or with siblings.
    const parentR = Math.sqrt(parentPos.x ** 2 + parentPos.y ** 2);
    const childR = parentR + 80;
    positions.set(node.id, {
      x: childR * Math.cos(childAngle),
      y: childR * Math.sin(childAngle),
    });
  }

  // Edge case: orphans whose parent isn't in the graph (stale reference
  // or filtered out). Drop them at category-default position so they
  // don't disappear — better to show them somewhere than to lose them.
  for (const n of nodes) {
    if (!positions.has(n.id)) {
      const cat = live.find((c) => c.id === n.catId);
      const angle = cat ? angles.get(cat.id) : undefined;
      if (angle !== undefined) {
        const a = (angle * Math.PI) / 180;
        positions.set(n.id, {
          x: R_LEAF_BASE * Math.cos(a),
          y: R_LEAF_BASE * Math.sin(a),
        });
      }
    }
  }

  return positions;
}

/** Tree layout for the pinned-category drill-down view. Standard
 *  Reingold-Tilford-lite: each leaf gets its own row; internal nodes
 *  centroid over their visible children. Returns { col, row } slots
 *  that the renderer multiplies by COLUMN_WIDTH / ROW_HEIGHT to get
 *  pixel positions. Hidden children (collapsed parents) are excluded
 *  from the row count, so the tree stays compact as the user explores.
 *
 *  `visibleSet` is the set of node ids that survived the
 *  expanded/collapsed filter — pass null to skip filtering.
 */
function layoutPinnedTree(
  roots: AtlasNode[],
  childrenByParent: Map<string, AtlasNode[]>,
  visibleSet: Set<string> | null
): { positions: Map<string, { col: number; row: number }>; rowCount: number; colCount: number } {
  const positions = new Map<string, { col: number; row: number }>();
  let rowCursor = 0;
  let maxCol = 0;

  const visit = (node: AtlasNode, depth: number): number => {
    maxCol = Math.max(maxCol, depth);
    const allChildren = childrenByParent.get(node.id) ?? [];
    const visibleChildren = visibleSet
      ? allChildren.filter((c) => visibleSet.has(c.id))
      : allChildren;
    if (visibleChildren.length === 0) {
      const row = rowCursor++;
      positions.set(node.id, { col: depth, row });
      return row;
    }
    const childRows: number[] = [];
    for (const c of visibleChildren) {
      childRows.push(visit(c, depth + 1));
    }
    const midRow = (Math.min(...childRows) + Math.max(...childRows)) / 2;
    positions.set(node.id, { col: depth, row: midRow });
    return midRow;
  };

  for (const root of roots) {
    visit(root, 0);
    rowCursor += 0.5; // tasteful gap between root sub-trees
  }
  return { positions, rowCount: rowCursor, colCount: maxCol + 1 };
}

function sectorWedgePath(cx: number, cy: number, angle: number): string {
  const r0 = R_CAT_LABEL;
  const r1 = R_OUTER_GUIDE;
  const spread = 50; // degrees
  const a0 = ((angle - spread / 2) * Math.PI) / 180;
  const a1 = ((angle + spread / 2) * Math.PI) / 180;
  const x0a = cx + r0 * Math.cos(a0);
  const y0a = cy + r0 * Math.sin(a0);
  const x0b = cx + r0 * Math.cos(a1);
  const y0b = cy + r0 * Math.sin(a1);
  const x1a = cx + r1 * Math.cos(a0);
  const y1a = cy + r1 * Math.sin(a0);
  const x1b = cx + r1 * Math.cos(a1);
  const y1b = cy + r1 * Math.sin(a1);
  return `M ${x0a} ${y0a} L ${x1a} ${y1a} A ${r1} ${r1} 0 0 1 ${x1b} ${y1b} L ${x0b} ${y0b} A ${r0} ${r0} 0 0 0 ${x0a} ${y0a} Z`;
}

// ─── Component props ──────────────────────────────────────────────────

export type AtlasTab = "explore" | "tensions";

interface Props {
  snapshot: GraphSnapshot | null;
  tenantId: string;
  mode: "onboarding" | "living";
  modeOverride: "auto" | "onboarding" | "living";
  onSetModeOverride: (next: "auto" | "onboarding" | "living") => void;
  tab: AtlasTab;
  onSetTab: (tab: AtlasTab) => void;
  loading: boolean;
  onRefresh: () => void;
  /** Switch the active brand. Called when the user submits a new tenant
   *  id via the "+ New brand" button. The page persists the new id and
   *  resets the chat session so the new brand starts fresh. */
  onChangeTenant?: (next: string) => void;
  /** Spotlight focus from the agent's most recent tool call(s). When
   * any node or category is in focus, everything else dims so the
   * user can SEE what the agent is reasoning about. Empty arrays =
   * no focus (atlas relaxes to neutral). */
  focus?: { nodeIds: string[]; serverCategories: string[] };
}

// ─── Component ────────────────────────────────────────────────────────

export function BrandAtlas({
  snapshot,
  tenantId,
  mode,
  modeOverride,
  onSetModeOverride,
  tab,
  onSetTab,
  onChangeTenant,
  loading,
  focus,
}: Props) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 1100, h: 800 });
  const [hover, setHover] = useState<string | null>(null);
  // "Brands" popover state. When open, we fetch the list of all
  // tenants from the server so the user can switch between them. The
  // input at the bottom of the popover creates a fresh tenant.
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [newBrandInput, setNewBrandInput] = useState("");
  const [brandList, setBrandList] = useState<BrandTenant[] | null>(null);
  const [brandListLoading, setBrandListLoading] = useState(false);
  const [brandListError, setBrandListError] = useState<string | null>(null);

  // Fetch the tenant list every time the popover opens — cheap query,
  // and we want fresh node counts when the user looks.
  useEffect(() => {
    if (!brandsOpen) return;
    let cancelled = false;
    setBrandListLoading(true);
    setBrandListError(null);
    void (async () => {
      try {
        const { tenants } = await api.graph.listTenants();
        if (!cancelled) setBrandList(tenants);
      } catch (e) {
        if (!cancelled) {
          setBrandListError(
            e instanceof Error ? e.message : "Failed to load brands."
          );
        }
      } finally {
        if (!cancelled) setBrandListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandsOpen]);
  // User-pinned category focus — set by clicking a category label.
  // When set, the atlas hard-zooms onto that sector and dims everything
  // else. Survives streaming spotlight changes (the agent's tool_use
  // events can't unpin it). Cleared by clicking the "Exit focus" pill
  // or by clicking the stage background outside any sector.
  const [pinnedCatId, setPinnedCatId] = useState<string | null>(null);
  // Two-phase pin animation: when a user pins a category, the camera
  // zooms in (~450ms) and THEN the radial atlas fades out + the tree
  // fades in. This preserves spatial continuity: the eye tracks the
  // sector during zoom, then sees its structure unfold in place.
  // `treeReady` flips true ~440ms after pin (matches the camera
  // transition duration), driving the crossfade.
  const [treeReady, setTreeReady] = useState(false);
  useEffect(() => {
    if (!pinnedCatId) {
      setTreeReady(false);
      return;
    }
    const t = setTimeout(() => setTreeReady(true), 440);
    return () => clearTimeout(t);
  }, [pinnedCatId]);
  // Depth filter — when set to a specific level, only nodes at that
  // tree depth render. Useful for separating Type-level from leaves
  // on busy graphs. "all" shows everything (default).
  const [depthFilter, setDepthFilter] = useState<"all" | 1 | 2 | 3>("all");
  // Per-node expansion state. By default, children are HIDDEN — only
  // top-level nodes render and parents show a clickable "+N" badge.
  // Clicking the badge adds the parent to this set; its children fan
  // out from it. Clicking again removes it. Independent per node so
  // multiple branches can be expanded simultaneously.
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(
    () => new Set()
  );
  const toggleExpansion = useCallback((nodeId: string) => {
    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);
  // Resize observer — keep SVG sized to its container
  useEffect(() => {
    const update = () => {
      if (!stageRef.current) return;
      const r = stageRef.current.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    if (stageRef.current) ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, []);

  // ─── Live category list + angles (data-driven, not fixed) ──────────
  const liveCategories = useMemo(() => buildLiveCategories(snapshot), [snapshot]);
  const angles = useMemo(() => computeAngles(liveCategories), [liveCategories]);
  const catById = useMemo<Record<string, AtlasCategory>>(
    () => Object.fromEntries(liveCategories.map((c) => [c.id, c])),
    [liveCategories]
  );

  // Tensions: which node ids participate in live contradictions. The
  // server returns the full GraphTension list on the snapshot.
  const tensions: GraphTension[] = useMemo(
    () => snapshot?.tensions ?? [],
    [snapshot]
  );
  const tensionNodeIds = useMemo(() => {
    const s = new Set<string>();
    for (const t of tensions) {
      s.add(t.node_a_id);
      s.add(t.node_b_id);
    }
    return s;
  }, [tensions]);

  // Spotlight focus → atlas-internal ids. The focus prop carries
  // server category names; we map those to the matching atlas category
  // id via `liveCategories[i].serverCats`. Node ids pass through.
  const focusedNodeIds = useMemo(
    () => new Set(focus?.nodeIds ?? []),
    [focus]
  );
  const focusedCatIds = useMemo(() => {
    const out = new Set<string>();
    for (const sc of focus?.serverCategories ?? []) {
      for (const c of liveCategories) {
        if (c.serverCats.includes(sc)) out.add(c.id);
      }
    }
    return out;
  }, [focus, liveCategories]);
  const focusActive =
    focusedNodeIds.size > 0 || focusedCatIds.size > 0;

  const atlasNodes = useMemo(
    () => mapSnapshotToAtlas(snapshot, liveCategories, tensionNodeIds),
    [snapshot, liveCategories, tensionNodeIds]
  );

  // Pinning a category auto-expands every parent inside it so the
  // user sees the FULL sub-tree, not collapsed teasers. Unpinning
  // collapses everything for a clean restart.
  const setPin = useCallback(
    (next: string | null) => {
      setPinnedCatId(next);
      if (next === null) {
        setExpandedNodeIds(new Set());
        return;
      }
      const childCounts = new Map<string, number>();
      for (const n of atlasNodes) {
        if (n.parentId) {
          childCounts.set(n.parentId, (childCounts.get(n.parentId) ?? 0) + 1);
        }
      }
      const idToParent = new Map<string, string | null>();
      const idToCat = new Map<string, string>();
      for (const n of atlasNodes) {
        idToParent.set(n.id, n.parentId ?? null);
        idToCat.set(n.id, n.catId);
      }
      const rootCatById = new Map<string, string>();
      for (const n of atlasNodes) {
        let cur = n.id;
        let hops = 0;
        while (hops < 5) {
          const p = idToParent.get(cur);
          if (!p) break;
          cur = p;
          hops++;
        }
        rootCatById.set(n.id, idToCat.get(cur) ?? n.catId);
      }
      const expandIds = new Set<string>();
      for (const n of atlasNodes) {
        const hasChildren = (childCounts.get(n.id) ?? 0) > 0;
        const inScope =
          n.catId === next || rootCatById.get(n.id) === next;
        if (hasChildren && inScope) expandIds.add(n.id);
      }
      setExpandedNodeIds(expandIds);
    },
    [atlasNodes]
  );

  const positions = useMemo(
    () => computeOverview(atlasNodes, liveCategories, angles),
    [atlasNodes, liveCategories, angles]
  );

  // Count children per parent so node chips can render a "+N children"
  // badge for containers. Built once per node-list change.
  const childCountByParent = useMemo(() => {
    const counts = new Map<string, number>();
    for (const n of atlasNodes) {
      if (n.parentId) {
        counts.set(n.parentId, (counts.get(n.parentId) ?? 0) + 1);
      }
    }
    return counts;
  }, [atlasNodes]);

  // Reverse lookup: nodeId → catId of its FURTHEST ancestor (the
  // root of its subtree). Used to decide pin-visibility for children
  // whose own catId might differ from the pinned category — they
  // should stay visible as long as their root belongs to the pin.
  const rootCatIdByNode = useMemo(() => {
    const idToParent = new Map<string, string | null>();
    const idToCat = new Map<string, string>();
    for (const n of atlasNodes) {
      idToParent.set(n.id, n.parentId ?? null);
      idToCat.set(n.id, n.catId);
    }
    const out = new Map<string, string>();
    for (const n of atlasNodes) {
      // Walk up to the root; max 5 hops as defense against cycles
      let cur = n.id;
      let hops = 0;
      while (hops < 5) {
        const parent = idToParent.get(cur);
        if (!parent) break;
        cur = parent;
        hops++;
      }
      const rootCat = idToCat.get(cur) ?? n.catId;
      out.set(n.id, rootCat);
    }
    return out;
  }, [atlasNodes]);

  // Visibility derived from expansion state. A node renders when
  //   - it's a root (no parentId), OR
  //   - every ancestor in its parent chain is in expandedNodeIds.
  // So a depth-3 leaf is hidden until BOTH its grandparent and parent
  // are expanded. Keeps the atlas readable when sub-trees are deep.
  const visibleNodeIds = useMemo(() => {
    const parentOf = new Map<string, string | null>();
    for (const n of atlasNodes) {
      parentOf.set(n.id, n.parentId ?? null);
    }
    const isAncestryExpanded = (id: string): boolean => {
      let cur = parentOf.get(id) ?? null;
      while (cur) {
        if (!expandedNodeIds.has(cur)) return false;
        cur = parentOf.get(cur) ?? null;
      }
      return true;
    };
    const out = new Set<string>();
    for (const n of atlasNodes) {
      if (!n.parentId || isAncestryExpanded(n.id)) {
        out.add(n.id);
      }
    }
    return out;
  }, [atlasNodes, expandedNodeIds]);

  // Per-node depth (1 = direct child of Brand, 2 = under a Type-level
  // parent, 3 = anchor under a Branch). Built by BFS from depth-1
  // roots through the parent_id graph.
  const depthByNodeId = useMemo(() => {
    const out = new Map<string, number>();
    const childrenByParent = new Map<string, string[]>();
    for (const n of atlasNodes) {
      if (n.parentId) {
        const arr = childrenByParent.get(n.parentId) ?? [];
        arr.push(n.id);
        childrenByParent.set(n.parentId, arr);
      }
    }
    const roots = atlasNodes.filter((n) => !n.parentId);
    const queue: Array<[string, number]> = roots.map((n) => [n.id, 1]);
    while (queue.length > 0) {
      const item = queue.shift();
      if (!item) break;
      const [id, d] = item;
      if (out.has(id)) continue;
      out.set(id, d);
      for (const childId of childrenByParent.get(id) ?? []) {
        queue.push([childId, d + 1]);
      }
    }
    return out;
  }, [atlasNodes]);

  // Counts per atlas category (including merged Color+ColorPalette etc.)
  const catCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const n of atlasNodes) {
      counts[n.catId] = (counts[n.catId] ?? 0) + 1;
    }
    return counts;
  }, [atlasNodes]);

  const totalNodes = atlasNodes.length;
  const paletteSwatches = useMemo(
    () =>
      atlasNodes
        .filter((n) => n.catId === "palette" && n.hex)
        .slice(0, 5)
        .map((n) => n.hex!),
    [atlasNodes]
  );
  const sparseCats = useMemo(
    // Only Tier-1 (alwaysShow) categories can be "sparse" — Tier-2
    // categories by definition have ≥2 nodes (or they wouldn't have
    // been proposed). So we surface sparse only for the canonical
    // structure the brand "should" have.
    () =>
      liveCategories.filter(
        (c) => c.alwaysShow && (catCounts[c.id] ?? 0) <= 1
      ),
    [liveCategories, catCounts]
  );

  // Camera — center origin at the middle of the stage.
  const ox = size.w / 2;
  const oy = size.h / 2;
  const project = (p: { x: number; y: number }) => ({
    x: p.x + ox,
    y: p.y + oy,
  });

  // Active = hovered node, used to highlight + dim others in 3.5c.
  // For 3.5a we just track for sector tinting.
  const activeCatId = hover ? atlasNodes.find((n) => n.id === hover)?.catId : null;

  // ─── Spotlight camera ──────────────────────────────────────────────
  // When focus is active, compute the centroid of all focused entities
  // and pan + zoom the atlas so that area sits ~at stage center. The
  // transform is applied to the visual wrapper so SVG + HTML overlays
  // animate together. Reset to identity when focus clears.
  //
  // User-pinned category focus takes precedence over the streaming
  // spotlight and uses a more aggressive zoom (1.7×) so the focused
  // sector really fills the frame.
  const cameraTransform = useMemo(() => {
    if (!focusActive && !pinnedCatId) {
      return {
        transform: "translate(0, 0) scale(1)",
        transformOrigin: "center center",
        transition: "transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)",
      };
    }
    // Collect focal points in atlas-local coords (relative to brand core)
    const points: { x: number; y: number }[] = [];
    // User-pinned category centroid takes precedence
    if (pinnedCatId) {
      const a = angles.get(pinnedCatId);
      if (a !== undefined) {
        const ar = (a * Math.PI) / 180;
        points.push({
          x: R_LEAF_BASE * Math.cos(ar),
          y: R_LEAF_BASE * Math.sin(ar),
        });
      }
    } else {
      for (const cat of liveCategories) {
        if (!focusedCatIds.has(cat.id)) continue;
        const a = angles.get(cat.id);
        if (a === undefined) continue;
        const ar = (a * Math.PI) / 180;
        // Use the leaf-ring midpoint as the category's effective center
        points.push({
          x: R_LEAF_BASE * Math.cos(ar),
          y: R_LEAF_BASE * Math.sin(ar),
        });
      }
      for (const n of atlasNodes) {
        if (!focusedNodeIds.has(n.id)) continue;
        const p = positions.get(n.id);
        if (p) points.push(p);
      }
    }
    if (points.length === 0) {
      return {
        transform: "translate(0, 0) scale(1)",
        transformOrigin: "center center",
        transition: "transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)",
      };
    }
    const fxLocal = points.reduce((s, p) => s + p.x, 0) / points.length;
    const fyLocal = points.reduce((s, p) => s + p.y, 0) / points.length;
    // Convert to screen coords
    const fxScreen = fxLocal + ox;
    const fyScreen = fyLocal + oy;
    // Camera math: with `transform-origin: center center`, after
    // scale(s) a point originally at (fx, fy) lands at
    // (sw/2 + s*(fx - sw/2), sh/2 + s*(fy - sh/2)). To bring that to
    // stage center we need translate(-s*(fx - sw/2), -s*(fy - sh/2)).
    // We multiply by `recenterFrac` < 1 so the focal area moves *near*
    // center instead of dead-on, leaving some context visible.
    // Pinned focus zooms harder (1.7×, recenter 0.95) so the focused
    // sector really fills the frame. Streaming spotlight stays gentler.
    const scale = pinnedCatId ? 1.7 : 1.32;
    const recenterFrac = pinnedCatId ? 0.95 : 0.85;
    const tx = -recenterFrac * (scale * (fxScreen - size.w / 2));
    const ty = -recenterFrac * (scale * (fyScreen - size.h / 2));
    return {
      transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
      transformOrigin: "center center",
      transition: "transform 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)",
    };
  }, [
    focusActive,
    pinnedCatId,
    focusedCatIds,
    focusedNodeIds,
    liveCategories,
    angles,
    atlasNodes,
    positions,
    ox,
    oy,
    size.w,
    size.h,
  ]);

  return (
    <div
      ref={stageRef}
      className="relative overflow-hidden h-full flex flex-col"
      style={{ background: "#fcfbf8", fontFamily: "Inter, sans-serif" }}
    >
      {/* Toolbar */}
      <div
        className="absolute top-0 left-0 right-0 px-6 py-4 z-10 flex items-start justify-between gap-3"
        style={{ pointerEvents: "none" }}
      >
        <div style={{ pointerEvents: "auto" }} className="min-w-0">
          <div
            className="text-[11px] font-medium uppercase"
            style={{ color: "#9a958d", letterSpacing: "0.06em" }}
          >
            Brand atlas
          </div>
          <div className="text-sm mt-0.5 flex items-center gap-2" style={{ color: "#1a1815" }}>
            <span>
              tenant {tenantId} ·{" "}
              <span style={{ color: "#6b665d" }}>
                {totalNodes} of {totalNodes} node{totalNodes === 1 ? "" : "s"}
              </span>
            </span>
            {/* Brands picker — opens a popover with the list of all
             *  existing tenants (click to switch) plus an input at the
             *  bottom for creating a fresh tenant. Submitting either
             *  way calls onChangeTenant; the page persists the new id
             *  and resets the chat session. */}
            {onChangeTenant && (
              <div className="relative" style={{ pointerEvents: "auto" }}>
                <button
                  type="button"
                  onClick={() => {
                    setBrandsOpen((o) => !o);
                    setNewBrandInput("");
                  }}
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                  style={{
                    background: brandsOpen ? "#1a1815" : "#fff",
                    color: brandsOpen ? "#fff" : "#1a1815",
                    border: "1px solid #e6e4df",
                    cursor: "pointer",
                    lineHeight: 1.5,
                  }}
                >
                  Brands
                  <span style={{ fontSize: 9, opacity: 0.65 }}>▾</span>
                </button>
                {brandsOpen && (
                  <div
                    className="absolute top-full left-0 mt-2 z-30"
                    style={{
                      background: "#fff",
                      border: "1px solid #e6e4df",
                      borderRadius: 8,
                      padding: 0,
                      boxShadow: "0 6px 24px rgba(0,0,0,.08)",
                      width: 280,
                      maxHeight: 460,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Brand list */}
                    <div
                      style={{
                        padding: "10px 10px 6px",
                        borderBottom: "1px solid #f0eee9",
                      }}
                    >
                      <div
                        className="text-[10px] font-medium uppercase"
                        style={{
                          color: "#9a958d",
                          letterSpacing: "0.06em",
                          marginBottom: 6,
                        }}
                      >
                        Existing brands
                      </div>
                      <div
                        style={{
                          maxHeight: 280,
                          overflowY: "auto",
                          margin: "0 -10px",
                        }}
                      >
                        {brandListLoading && (
                          <div
                            className="text-[11px] px-2.5 py-2"
                            style={{ color: "#9a958d" }}
                          >
                            Loading…
                          </div>
                        )}
                        {brandListError && (
                          <div
                            className="text-[11px] px-2.5 py-2"
                            style={{ color: "#b04020" }}
                          >
                            {brandListError}
                          </div>
                        )}
                        {!brandListLoading &&
                          !brandListError &&
                          brandList &&
                          brandList.length === 0 && (
                            <div
                              className="text-[11px] px-2.5 py-2"
                              style={{ color: "#9a958d" }}
                            >
                              No brands yet — create one below.
                            </div>
                          )}
                        {!brandListLoading &&
                          !brandListError &&
                          brandList &&
                          brandList.map((b) => {
                            const isCurrent = b.tenant_id === tenantId;
                            return (
                              <button
                                key={b.tenant_id}
                                type="button"
                                onClick={() => {
                                  if (!isCurrent) {
                                    onChangeTenant(b.tenant_id);
                                  }
                                  setBrandsOpen(false);
                                }}
                                className="w-full text-left flex items-center justify-between gap-2"
                                style={{
                                  padding: "6px 12px",
                                  background: isCurrent
                                    ? "#f6f4ef"
                                    : "transparent",
                                  border: "none",
                                  cursor: isCurrent ? "default" : "pointer",
                                  fontFamily: "Inter, sans-serif",
                                }}
                                onMouseEnter={(e) => {
                                  if (!isCurrent) {
                                    (e.currentTarget as HTMLButtonElement).style.background =
                                      "#faf8f3";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isCurrent) {
                                    (e.currentTarget as HTMLButtonElement).style.background =
                                      "transparent";
                                  }
                                }}
                              >
                                <span
                                  className="text-[12px]"
                                  style={{
                                    color: "#1a1815",
                                    fontFamily: "ui-monospace, monospace",
                                    fontWeight: isCurrent ? 600 : 400,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {b.tenant_id}
                                </span>
                                <span
                                  className="text-[10px] tabular-nums"
                                  style={{ color: "#9a958d", flexShrink: 0 }}
                                >
                                  {b.node_count} node
                                  {b.node_count === 1 ? "" : "s"}
                                  {isCurrent && (
                                    <span
                                      style={{
                                        marginLeft: 6,
                                        color: "#1a1815",
                                        fontWeight: 600,
                                      }}
                                    >
                                      ●
                                    </span>
                                  )}
                                </span>
                              </button>
                            );
                          })}
                      </div>
                    </div>

                    {/* New brand input */}
                    <div style={{ padding: 10 }}>
                      <div
                        className="text-[10px] font-medium uppercase"
                        style={{
                          color: "#9a958d",
                          letterSpacing: "0.06em",
                          marginBottom: 6,
                        }}
                      >
                        Create new brand
                      </div>
                      <input
                        autoFocus
                        type="text"
                        placeholder="e.g. acme-co"
                        value={newBrandInput}
                        onChange={(e) => setNewBrandInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && newBrandInput.trim()) {
                            onChangeTenant(newBrandInput);
                            setBrandsOpen(false);
                          } else if (e.key === "Escape") {
                            setBrandsOpen(false);
                          }
                        }}
                        className="w-full text-sm px-2 py-1.5"
                        style={{
                          border: "1px solid #e6e4df",
                          borderRadius: 6,
                          outline: "none",
                          fontFamily: "ui-monospace, monospace",
                        }}
                      />
                      <div className="flex justify-end gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => setBrandsOpen(false)}
                          className="text-[11px] px-2 py-1 rounded"
                          style={{
                            background: "transparent",
                            color: "#6b665d",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={!newBrandInput.trim()}
                          onClick={() => {
                            if (newBrandInput.trim()) {
                              onChangeTenant(newBrandInput);
                              setBrandsOpen(false);
                            }
                          }}
                          className="text-[11px] font-medium px-2.5 py-1 rounded"
                          style={{
                            background: newBrandInput.trim()
                              ? "#1a1815"
                              : "#e6e4df",
                            color: newBrandInput.trim() ? "#fff" : "#9a958d",
                            border: "none",
                            cursor: newBrandInput.trim() ? "pointer" : "default",
                          }}
                        >
                          Start fresh
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Explore / Tensions tabs */}
          <div
            className="mt-3 inline-flex items-center rounded-full p-0.5 text-sm border"
            style={{ background: "#fff", borderColor: "#e6e4df" }}
          >
            <button
              onClick={() => onSetTab("explore")}
              className={cn("px-3 py-1 rounded-full font-medium transition-colors text-[12px]")}
              style={{
                background: tab === "explore" ? "#1a1815" : "transparent",
                color: tab === "explore" ? "#fff" : "#6b665d",
                border: "none",
                cursor: "pointer",
              }}
            >
              Explore
            </button>
            <button
              onClick={() => onSetTab("tensions")}
              className={cn("px-3 py-1 rounded-full font-medium transition-colors text-[12px]")}
              style={{
                background: tab === "tensions" ? "#1a1815" : "transparent",
                color: tab === "tensions" ? "#fff" : "#6b665d",
                border: "none",
                cursor: "pointer",
              }}
            >
              Tensions
            </button>
          </div>
        </div>
        <div style={{ pointerEvents: "auto" }} className="flex items-center gap-2">
          {/* Depth filter — segmented "All / 1 / 2 / 3". L1 = direct
              children of Brand, L2 = branches, L3 = leaves. */}
          <DepthFilterPill value={depthFilter} onChange={setDepthFilter} />
          <ModePill mode={mode} override={modeOverride} onSet={onSetModeOverride} />
        </div>
      </div>

      {/* Exit focus pill — floats top-left when a category is pinned. */}
      {pinnedCatId && (
        <button
          type="button"
          onClick={() => setPin(null)}
          className="absolute z-20 inline-flex items-center gap-1.5"
          style={{
            top: 88,
            left: 22,
            background: "#fff",
            border: "1px solid #e6e4df",
            borderRadius: 999,
            padding: "5px 12px 5px 9px",
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: "#1a1815",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,.06)",
          }}
        >
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="#6b665d" strokeWidth="1.6" strokeLinecap="round">
            <path d="M7 2L3 5.5L7 9" />
          </svg>
          <span>
            Exit{" "}
            <span style={{ fontWeight: 600 }}>
              {catById[pinnedCatId]?.label ?? pinnedCatId}
            </span>
          </span>
        </button>
      )}

      {/* Pinned-category drill-down tree (option B): emerges after the
       *  camera-zoom finishes, fades in with a subtle scale-up so it
       *  feels like the sector is unfolding into structure. Mounted
       *  only while pinned to keep the DOM lean. */}
      {pinnedCatId && (
        <div
          className="absolute inset-0"
          style={{
            opacity: treeReady ? 1 : 0,
            transform: treeReady ? "scale(1)" : "scale(0.94)",
            transition: "opacity 320ms ease-out, transform 380ms cubic-bezier(0.2, 0.8, 0.2, 1)",
            pointerEvents: treeReady ? "auto" : "none",
          }}
        >
          <PinnedTreeView
            pinnedCatId={pinnedCatId}
            allNodes={atlasNodes}
            catById={catById}
            rootCatIdByNode={rootCatIdByNode}
            childCountByParent={childCountByParent}
            expandedNodeIds={expandedNodeIds}
            toggleExpansion={toggleExpansion}
            hover={hover}
            setHover={setHover}
            focusedNodeIds={focusedNodeIds}
            focusedCatIds={focusedCatIds}
            containerW={size.w}
            containerH={size.h}
          />
        </div>
      )}

      {/* Transformed visual atlas — SVG + brand core + category labels
          + nodes all share this wrapper so the camera transform (pan +
          zoom toward the focused area) applies to everything together
          without each overlay needing to know about the camera. Stays
          mounted during pin and zooms in via cameraTransform; once the
          zoom completes (treeReady), it fades out and the tree takes
          over visually. */}
      <div
        className="absolute inset-0"
        style={{
          opacity: pinnedCatId && treeReady ? 0 : 1,
          transition: "opacity 280ms ease-in",
          pointerEvents: pinnedCatId && treeReady ? "none" : "auto",
        }}
      >
      <div className="absolute inset-0" style={cameraTransform}>
      {/* SVG layer: rings + sector wedges */}
      <svg
        width={size.w}
        height={size.h}
        className="absolute inset-0"
        style={{ pointerEvents: "none" }}
      >
        <defs>
          <radialGradient id="atlas-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fff" />
            <stop offset="100%" stopColor="#f4f3ef" />
          </radialGradient>
        </defs>

        {/* Concentric guide rings */}
        {[R_CAT_LABEL, R_LEAF_BASE, R_OUTER_GUIDE].map((r, i) => (
          <circle
            key={i}
            cx={ox}
            cy={oy}
            r={r}
            fill="none"
            stroke="#e6e4df"
            strokeOpacity={i === 2 ? 0.5 : 1}
            strokeDasharray={i === 2 ? "2 5" : undefined}
            strokeWidth={1}
          />
        ))}

        {/* Sector wedge fills — faintly tinted by category hue.
            Focused sector lights up brighter than hover. */}
        {liveCategories.map((c) => {
          const angle = angles.get(c.id);
          if (angle === undefined) return null;
          const isFocused = focusedCatIds.has(c.id);
          const isHovered = activeCatId === c.id;
          const op = isFocused ? 0.18 : isHovered ? 0.1 : 0.025;
          return (
            <path
              key={c.id}
              d={sectorWedgePath(ox, oy, angle)}
              fill={oklch(0.62, 0.14, c.hue, op)}
              style={{ transition: "fill .25s" }}
            />
          );
        })}

        {/* Core circle */}
        <circle cx={ox} cy={oy} r={R_CORE} fill="url(#atlas-core)" stroke="#e6e4df" />

        {/* Parent → child structural curves. Thin gray Béziers drawn
            behind nodes so users can see the tree structure (e.g. a
            "Color grading" branch with its anchors hanging off it)
            without the lines competing with the chips for attention.
            Same-category edges curve gently outward; cross-category
            edges (rare via propose_link_edit) sweep through the
            brand-core area. Dimmed on the Tensions tab + when a
            spotlight focus is active and neither endpoint is in it. */}
        {atlasNodes.map((n) => {
          if (!n.parentId) return null;
          // Both endpoints must be visible — skip curves to hidden
          // (un-expanded) children so we don't dangle lines into empty space.
          if (!visibleNodeIds.has(n.id) || !visibleNodeIds.has(n.parentId)) {
            return null;
          }
          const pa = positions.get(n.parentId);
          const pb = positions.get(n.id);
          if (!pa || !pb) return null;
          const sa = project(pa);
          const sb = project(pb);
          const parentNode = atlasNodes.find((x) => x.id === n.parentId);
          const sameCat = parentNode && parentNode.catId === n.catId;
          // Same-category edge: bow gently outward (control point
          // slightly further from the core than the midpoint) so the
          // line doesn't run straight through the parent chip's text.
          const mx = (sa.x + sb.x) / 2;
          const my = (sa.y + sb.y) / 2;
          const dirX = mx - ox;
          const dirY = my - oy;
          const dirLen = Math.max(Math.sqrt(dirX * dirX + dirY * dirY), 1);
          const cpx = sameCat
            ? mx + (dirX / dirLen) * 25
            : mx + (ox - mx) * 0.5;
          const cpy = sameCat
            ? my + (dirY / dirLen) * 25
            : my + (oy - my) * 0.5;
          // Dim when on Tensions tab (tensions are the point of that
          // view) or when spotlight is active and neither endpoint is
          // focused.
          const eitherFocused =
            focusedNodeIds.has(n.id) ||
            focusedNodeIds.has(n.parentId) ||
            focusedCatIds.has(n.catId);
          let opacity = 0.28;
          if (tab === "tensions") opacity = 0.06;
          else if (focusActive && !eitherFocused) opacity = 0.08;
          else if (focusActive && eitherFocused) opacity = 0.55;
          return (
            <path
              key={`edge-${n.parentId}-${n.id}`}
              d={`M ${sa.x} ${sa.y} Q ${cpx} ${cpy} ${sb.x} ${sb.y}`}
              fill="none"
              stroke="#9a958d"
              strokeOpacity={opacity}
              strokeWidth={eitherFocused ? 1.4 : 1}
              style={{ transition: "stroke-opacity .2s" }}
            />
          );
        })}

        {/* Tension arcs — red dashed Bézier curves between conflicting
            node positions. Control point biased toward the brand core
            so cross-category arcs sweep through the middle rather than
            cutting straight across. In the Tensions tab they get a
            glow filter; in Explore tab they're more subtle. */}
        {tensions.map((t) => {
          const pa = positions.get(t.node_a_id);
          const pb = positions.get(t.node_b_id);
          if (!pa || !pb) return null;
          const sa = project(pa);
          const sb = project(pb);
          // Same-category tensions: straight midpoint. Cross-category:
          // pull the control point halfway toward the core so the arc
          // bends through the middle.
          const nodeA = atlasNodes.find((n) => n.id === t.node_a_id);
          const nodeB = atlasNodes.find((n) => n.id === t.node_b_id);
          const sameCat = nodeA && nodeB && nodeA.catId === nodeB.catId;
          const mx = (sa.x + sb.x) / 2;
          const my = (sa.y + sb.y) / 2;
          const cpx = sameCat ? mx : (mx + (ox - mx) * 0.6);
          const cpy = sameCat ? my : (my + (oy - my) * 0.6);
          const isTensionsTab = tab === "tensions";
          return (
            <path
              key={t.proposal_id}
              d={`M ${sa.x} ${sa.y} Q ${cpx} ${cpy} ${sb.x} ${sb.y}`}
              fill="none"
              stroke="#c64a2c"
              strokeOpacity={isTensionsTab ? 0.95 : 0.45}
              strokeWidth={isTensionsTab ? 1.6 : 1.2}
              strokeDasharray="4 3"
            />
          );
        })}
      </svg>

      {/* Brand core content (HTML for crisp text) */}
      <BrandCore
        ox={ox}
        oy={oy}
        tenantId={tenantId}
        totalNodes={totalNodes}
        paletteSwatches={paletteSwatches}
      />

      {/* Category labels — clickable to pin user focus on that sector.
       *  When a category is pinned, skip its own floating label: the
       *  Exit-focus pill at top-left already shows the pinned name and
       *  the pin-zoom (1.7×) would otherwise pull the floating label
       *  on top of the chips. */}
      {liveCategories.map((c) => {
        if (pinnedCatId === c.id) return null;
        const angle = angles.get(c.id);
        if (angle === undefined) return null;
        const a = (angle * Math.PI) / 180;
        const r = R_CAT_LABEL + 35;
        const x = r * Math.cos(a) + ox;
        const y = r * Math.sin(a) + oy;
        const count = catCounts[c.id] ?? 0;
        const sparse = c.alwaysShow && count <= 1;
        const isStreamFocused = focusedCatIds.has(c.id);
        const isPinned = pinnedCatId === c.id;
        // Dim labels for non-focused categories when a focus is active
        const dimmedByStream =
          focusActive && !isStreamFocused && focusedNodeIds.size === 0;
        // When a category is pinned, dim every other category's label.
        const dimmedByPin = !!pinnedCatId && !isPinned;
        return (
          <CategoryLabel
            key={c.id}
            cat={c}
            x={x}
            y={y}
            count={count}
            sparse={sparse}
            hoverActive={activeCatId === c.id || isStreamFocused || isPinned}
            dimmed={dimmedByStream || dimmedByPin}
            pinned={isPinned}
            onClick={() => {
              // Click to pin / unpin this category. Pinning gives a
              // hard zoom on this sector; clicking the pinned label
              // again exits focus (which also collapses any expanded
              // sub-trees so we start fresh next time).
              setPin(isPinned ? null : c.id);
            }}
          />
        );
      })}

      {/* Nodes */}
      {atlasNodes.map((n) => {
        const p = positions.get(n.id);
        if (!p) return null;
        const sp = project(p);
        const cat = catById[n.catId];
        if (!cat) return null;

        // Hide children whose parent isn't expanded. Keeps the default
        // view to top-level nodes only; user expands sub-trees by
        // clicking the "+N" badge.
        if (!visibleNodeIds.has(n.id)) return null;

        // Depth filter — hide nodes that don't match the selected level.
        if (depthFilter !== "all") {
          const d = depthByNodeId.get(n.id) ?? 1;
          if (d !== depthFilter) return null;
        }

        // Three independent reasons a node might dim:
        //   1. Tensions tab and this isn't a tension node
        //   2. Streaming spotlight active and this isn't focused
        //   3. User-pinned category focus active and this node isn't in
        //      the pinned category's SUBTREE (matched by root catId,
        //      not own catId — children with different categories that
        //      hang off the pinned category stay visible)
        // A node is "focused" when its own id is in focusedNodeIds OR
        // its category is in focusedCatIds. Tension nodes never dim
        // due to spotlight — they're always relevant context.
        const isFocused =
          focusedNodeIds.has(n.id) || focusedCatIds.has(n.catId);
        const rootCatId = rootCatIdByNode.get(n.id) ?? n.catId;
        const isInPinnedScope =
          !!pinnedCatId &&
          (n.catId === pinnedCatId || rootCatId === pinnedCatId);
        const dimmedByTab = tab === "tensions" && !n.tension;
        const dimmedBySpotlight =
          focusActive && !isFocused && !n.tension && !isInPinnedScope;
        const dimmedByPin =
          !!pinnedCatId && !isInPinnedScope && !n.tension;
        const dimmed = dimmedByTab || dimmedBySpotlight || dimmedByPin;
        const childCount = childCountByParent.get(n.id) ?? 0;
        const isExpanded = expandedNodeIds.has(n.id);
        return (
          <NodeChip
            key={n.id}
            node={n}
            cat={cat}
            x={sp.x}
            y={sp.y}
            isHovered={hover === n.id || isFocused}
            dimmed={dimmed}
            // Glow ring is for AGENT-DRIVEN focus only. User-pinning a
            // category brightens the sector via the camera + dim logic
            // but doesn't ring every chip — that'd be visual noise.
            spotlightFocused={isFocused}
            childCount={childCount}
            expanded={isExpanded}
            onToggleExpand={
              childCount > 0 ? () => toggleExpansion(n.id) : undefined
            }
            onHover={setHover}
          />
        );
      })}

      </div>
      </div>
      {/* end transformed visual atlas wrapper */}

      {/* Coverage card — top-right floating, sparse sectors + tension
          count. Matches the mockup. Hidden in the Tensions tab so the
          arcs are the focus. */}
      {tab === "explore" && (sparseCats.length > 0 || tensions.length > 0) && (
        <CoverageCard
          sparseCats={sparseCats}
          tensionCount={tensions.length}
          onSwitchToTensions={
            tensions.length > 0 ? () => onSetTab("tensions") : undefined
          }
        />
      )}

      {/* Tensions tab: when zero tensions, show an explanatory empty state */}
      {tab === "tensions" && tensions.length === 0 && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          style={{ background: "rgba(252,251,248,0.7)" }}
        >
          <div
            className="text-center max-w-md px-6"
            style={{
              background: "#fff",
              border: "1px solid #e6e4df",
              borderRadius: 12,
              padding: "20px 24px",
              pointerEvents: "auto",
              boxShadow: "0 2px 12px rgba(0,0,0,.04)",
            }}
          >
            <div className="text-sm font-medium" style={{ color: "#1a1815" }}>
              No tensions on the graph
            </div>
            <div className="text-xs mt-2" style={{ color: "#6b665d" }}>
              When the agent flags two conflicting nodes via
              flag_contradiction, they'll appear here with a red dashed
              arc between them.
            </div>
          </div>
        </div>
      )}

      {/* Refreshing indicator */}
      {loading && (
        <div
          className="absolute top-3 right-3 text-[11px] inline-flex items-center gap-1.5 z-20"
          style={{
            color: "#6b665d",
            background: "#fff",
            border: "1px solid #e6e4df",
            padding: "3px 8px",
            borderRadius: 999,
          }}
        >
          <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
          refreshing
        </div>
      )}
    </div>
  );
}

// ─── Brand core ───────────────────────────────────────────────────────

function BrandCore({
  ox,
  oy,
  tenantId,
  totalNodes,
  paletteSwatches,
}: {
  ox: number;
  oy: number;
  tenantId: string;
  totalNodes: number;
  paletteSwatches: string[];
}) {
  return (
    <div
      style={{
        position: "absolute",
        left: ox,
        top: oy,
        transform: "translate(-50%, -50%)",
        width: R_CORE * 2,
        height: R_CORE * 2,
        borderRadius: "50%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        fontFamily: "Inter, sans-serif",
        zIndex: 2,
      }}
    >
      {paletteSwatches.length > 0 && (
        <div style={{ display: "flex", gap: 3 }}>
          {paletteSwatches.map((hex, i) => (
            <span
              key={i}
              style={{
                width: 11,
                height: 11,
                borderRadius: 6,
                background: hex,
                border: "1px solid rgba(0,0,0,.06)",
              }}
            />
          ))}
        </div>
      )}
      <div style={{ fontSize: 12, color: "#1a1815", fontWeight: 600, letterSpacing: "-0.01em" }}>
        tenant {tenantId}
      </div>
      <div
        style={{
          fontSize: 9.5,
          color: "#9a958d",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {totalNodes} {totalNodes === 1 ? "node" : "nodes"}
      </div>
    </div>
  );
}

// ─── Category label ───────────────────────────────────────────────────

function CategoryLabel({
  cat,
  x,
  y,
  count,
  sparse,
  hoverActive,
  dimmed,
  pinned,
  onClick,
}: {
  cat: AtlasCategory;
  x: number;
  y: number;
  count: number;
  sparse: boolean;
  hoverActive: boolean;
  dimmed?: boolean;
  pinned?: boolean;
  onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        position: "absolute",
        left: x,
        top: y,
        transform: "translate(-50%, -50%)",
        fontFamily: "Inter, sans-serif",
        fontSize: 11,
        fontWeight: 600,
        color: pinned
          ? oklch(0.35, 0.14, cat.hue)
          : oklch(0.45, 0.14, cat.hue),
        background: pinned ? oklch(0.97, 0.04, cat.hue) : "#fff",
        padding: "4px 10px",
        borderRadius: 999,
        border: `1px solid ${oklch(
          0.62,
          0.14,
          cat.hue,
          pinned ? 0.7 : hoverActive ? 0.55 : 0.25
        )}`,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        display: "flex",
        alignItems: "center",
        gap: 6,
        zIndex: 3,
        whiteSpace: "nowrap",
        transition: "border-color .15s, opacity .25s, background-color .15s",
        opacity: dimmed ? 0.3 : 1,
        cursor: onClick ? "pointer" : "default",
        boxShadow: pinned
          ? `0 0 0 5px ${oklch(0.62, 0.14, cat.hue, 0.22)}`
          : hoverActive
            ? `0 0 0 4px ${oklch(0.62, 0.14, cat.hue, 0.18)}`
            : "none",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 5,
          background: oklch(0.62, 0.14, cat.hue),
        }}
      />
      {cat.label}
      <span
        style={{
          fontWeight: 500,
          color: "#9a958d",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count}
      </span>
      {sparse && (
        <span
          style={{
            fontSize: 9.5,
            fontWeight: 500,
            color: "#8a7400",
            background: "#fff8d6",
            padding: "1px 6px",
            borderRadius: 4,
            marginLeft: 2,
          }}
        >
          sparse
        </span>
      )}
    </div>
  );
}

// ─── Node chip ────────────────────────────────────────────────────────

function NodeChip({
  node,
  cat,
  x,
  y,
  isHovered,
  dimmed,
  spotlightFocused,
  childCount,
  expanded,
  onToggleExpand,
  onHover,
}: {
  node: AtlasNode;
  cat: AtlasCategory;
  x: number;
  y: number;
  isHovered: boolean;
  dimmed: boolean;
  spotlightFocused?: boolean;
  /** When >0, this node is a container — render a small "+N" badge
   *  to signal that children hang off it. */
  childCount?: number;
  /** Whether the user has expanded this parent (revealing its children). */
  expanded?: boolean;
  /** Called when the user clicks the +N badge. Undefined for leaf nodes. */
  onToggleExpand?: () => void;
  onHover: (id: string | null) => void;
}) {
  const isColor = node.catId === "palette" && node.hex;
  const lowConf = node.conf < 0.75;
  const tension = node.tension;

  // Border styling priorities: tension wins (red dashed), then low-conf
  // dashed gray, then solid gray.
  const borderColor = isColor
    ? "rgba(0,0,0,.1)"
    : tension
      ? "#c64a2c"
      : lowConf
        ? "#e6e4df"
        : "#d8d4cb";
  const borderStyle = !isColor && (lowConf || tension) ? "dashed" : "solid";
  const borderWidth = tension && !isColor ? "1.5px" : "1px";

  return (
    <div
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
      title={node.label}
      style={{
        position: "absolute",
        left: x,
        top: y,
        transform: `translate(-50%, -50%) scale(${spotlightFocused ? 1.12 : isHovered ? 1.08 : 1})`,
        background: isColor ? node.hex! : "#fff",
        color: isColor
          ? readableOn(node.hex!)
          : tension
            ? "#b04020"
            : "#1a1815",
        padding: isColor ? "5px 9px" : "6px 11px",
        borderRadius: 999,
        border: `${borderWidth} ${borderStyle} ${borderColor}`,
        fontFamily: isColor ? "ui-monospace, monospace" : "Inter, sans-serif",
        fontSize: isColor ? 11.5 : 12.5,
        fontWeight: 500,
        cursor: "pointer",
        userSelect: "none",
        whiteSpace: "nowrap",
        opacity: dimmed ? 0.2 : 1,
        // Spotlight focus = full color hue glow (stronger than hover);
        // hover = the same color glow at lower intensity. Both coexist
        // so a focused node the user also hovers gets maximally lit.
        boxShadow: spotlightFocused
          ? `0 0 0 4px ${oklch(0.62, 0.14, cat.hue, 0.45)}, 0 4px 10px rgba(0,0,0,.06)`
          : isHovered
            ? `0 0 0 3px ${oklch(0.62, 0.14, cat.hue, 0.3)}, 0 2px 6px rgba(0,0,0,.06)`
            : "0 1px 2px rgba(0,0,0,.04)",
        transition: "transform .15s, box-shadow .25s, opacity .25s",
        zIndex: spotlightFocused ? 6 : isHovered ? 5 : 2,
        display: "flex",
        alignItems: "center",
        gap: 5,
      }}
    >
      {!isColor && (
        <span
          style={{
            width: 5,
            height: 5,
            borderRadius: 3,
            background: tension ? "#c64a2c" : oklch(0.62, 0.14, cat.hue),
            flexShrink: 0,
          }}
        />
      )}
      <span>{isColor ? node.hex : node.label}</span>
      {childCount !== undefined && childCount > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand?.();
          }}
          style={{
            marginLeft: 2,
            padding: "1px 6px",
            borderRadius: 999,
            background: expanded
              ? `oklch(0.62 0.14 ${cat.hue} / 0.35)`
              : `oklch(0.62 0.14 ${cat.hue} / 0.18)`,
            color: expanded
              ? `oklch(0.25 0.14 ${cat.hue})`
              : `oklch(0.35 0.14 ${cat.hue})`,
            fontSize: 10,
            fontWeight: 600,
            fontFamily: "Inter, sans-serif",
            fontVariantNumeric: "tabular-nums",
            border: `1px solid oklch(0.62 0.14 ${cat.hue} / ${expanded ? 0.5 : 0.25})`,
            lineHeight: 1.2,
            flexShrink: 0,
            cursor: onToggleExpand ? "pointer" : "default",
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
          }}
          title={
            expanded
              ? `Click to collapse ${childCount} children`
              : `Click to expand ${childCount} child${childCount === 1 ? "" : "ren"}`
          }
        >
          <span style={{ fontSize: 9, opacity: 0.85 }}>{expanded ? "−" : "+"}</span>
          {childCount}
        </button>
      )}
      {tension && (
        <svg
          width="9"
          height="9"
          viewBox="0 0 10 10"
          style={{ marginLeft: 2, flexShrink: 0 }}
        >
          <path d="M1 9 L5 1 L9 9 Z" fill="none" stroke="#c64a2c" strokeWidth="1.3" />
          <path d="M5 4 L5 6.5" stroke="#c64a2c" strokeWidth="1.3" />
          <circle cx="5" cy="7.6" r=".7" fill="#c64a2c" />
        </svg>
      )}
    </div>
  );
}

// ─── Pinned tree view ─────────────────────────────────────────────────
// When the user pins a category, this REPLACES the radial atlas.
// Rationale: radial layouts are great for at-a-glance overview but
// terrible for parent→child drill-down (hierarchy encoded as angle +
// radius is hard for the eye to parse). The tree view trades the
// orbital context for clean, scannable LTR hierarchy.

function PinnedTreeView({
  pinnedCatId,
  allNodes,
  catById,
  rootCatIdByNode,
  childCountByParent,
  expandedNodeIds,
  toggleExpansion,
  hover,
  setHover,
  focusedNodeIds,
  focusedCatIds,
  containerW,
  containerH,
}: {
  pinnedCatId: string;
  allNodes: AtlasNode[];
  catById: Record<string, AtlasCategory>;
  rootCatIdByNode: Map<string, string>;
  childCountByParent: Map<string, number>;
  expandedNodeIds: Set<string>;
  toggleExpansion: (id: string) => void;
  hover: string | null;
  setHover: (id: string | null) => void;
  focusedNodeIds: Set<string>;
  focusedCatIds: Set<string>;
  containerW: number;
  containerH: number;
}) {
  // ─── Scope filter: every node whose root category matches the pin ──
  // Roots themselves have rootCatId === own catId, so this catches both
  // direct PhotographyStyle roots AND any Tier-2 (e.g. Color Grading)
  // that hangs off a PhotographyStyle parent.
  const scopeNodes = allNodes.filter(
    (n) =>
      n.catId === pinnedCatId || rootCatIdByNode.get(n.id) === pinnedCatId
  );

  // ─── Build parent→children map (only for in-scope nodes) ─────────────
  const childrenByParent = new Map<string, AtlasNode[]>();
  const scopeIds = new Set(scopeNodes.map((n) => n.id));
  for (const n of scopeNodes) {
    if (n.parentId && scopeIds.has(n.parentId)) {
      const arr = childrenByParent.get(n.parentId) ?? [];
      arr.push(n);
      childrenByParent.set(n.parentId, arr);
    }
  }

  // ─── Visibility filter via expansion state ───────────────────────────
  // Walk from roots; include a child only when its parent is expanded.
  const roots = scopeNodes.filter(
    (n) => !n.parentId || !scopeIds.has(n.parentId)
  );
  const visibleSet = new Set<string>();
  const queue: AtlasNode[] = [...roots];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    visibleSet.add(cur.id);
    if (expandedNodeIds.has(cur.id)) {
      const kids = childrenByParent.get(cur.id) ?? [];
      queue.push(...kids);
    }
  }

  // ─── Layout ──────────────────────────────────────────────────────────
  const { positions, rowCount, colCount } = layoutPinnedTree(
    roots,
    childrenByParent,
    visibleSet
  );

  // Physical dimensions: wide columns so chips have breathing room,
  // tall rows so labels don't smash into each other vertically.
  const COL_W = 300;
  const ROW_H = 64;
  // Chips extend ±~150px from their centered position (translate -50%),
  // so the leftmost column's CENTER needs ~170px of left margin to keep
  // the chip's left edge on-screen. Same on the right. When the tree
  // fits in the container we center it; otherwise the wrapper scrolls.
  const CHIP_HALF = 170;
  const contentW = (colCount - 1) * COL_W + CHIP_HALF * 2;
  const contentH = Math.max(rowCount, 1) * ROW_H;
  const PAD_LEFT = Math.max(CHIP_HALF, (containerW - contentW) / 2 + CHIP_HALF);
  const PAD_TOP = Math.max(100, (containerH - contentH) / 2);

  const treeW = Math.max(contentW, containerW);
  const treeH = Math.max(PAD_TOP + contentH + 60, containerH);

  // Empty state: pinned category has no nodes yet.
  if (scopeNodes.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="text-center max-w-md px-6"
          style={{
            background: "#fff",
            border: "1px solid #e6e4df",
            borderRadius: 12,
            padding: "20px 24px",
            boxShadow: "0 2px 12px rgba(0,0,0,.04)",
          }}
        >
          <div className="text-sm font-medium" style={{ color: "#1a1815" }}>
            No nodes in {catById[pinnedCatId]?.label ?? pinnedCatId} yet
          </div>
          <div className="text-xs mt-2" style={{ color: "#6b665d" }}>
            Tell the chat what to add — e.g. "the brand voice is warm and
            confident" or attach a moodboard image.
          </div>
        </div>
      </div>
    );
  }

  // Helper: pixel position of a node by id (or undefined if hidden)
  const pixelOf = (id: string) => {
    const pos = positions.get(id);
    if (!pos) return undefined;
    return {
      x: PAD_LEFT + pos.col * COL_W,
      y: PAD_TOP + pos.row * ROW_H,
    };
  };

  // Build connector list: each visible parent → each visible child
  const connectors: Array<{ from: { x: number; y: number }; to: { x: number; y: number }; hue: number }> = [];
  for (const n of scopeNodes) {
    if (!visibleSet.has(n.id)) continue;
    const from = pixelOf(n.id);
    if (!from) continue;
    const kids = childrenByParent.get(n.id) ?? [];
    for (const kid of kids) {
      if (!visibleSet.has(kid.id)) continue;
      const to = pixelOf(kid.id);
      if (!to) continue;
      const hue = catById[kid.catId]?.hue ?? 200;
      connectors.push({ from, to, hue });
    }
  }

  return (
    <div
      className="absolute inset-0 overflow-auto"
      style={{ background: "#fcfbf8" }}
    >
      <div
        style={{
          position: "relative",
          width: Math.max(treeW, containerW),
          height: treeH,
        }}
      >
        {/* SVG connector layer */}
        <svg
          width="100%"
          height="100%"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
          }}
        >
          {connectors.map((c, i) => {
            // Bezier with horizontal control points → S-curve that
            // reads as parent→child without sharp angles.
            const dx = (c.to.x - c.from.x) * 0.5;
            const path = `M ${c.from.x + 90} ${c.from.y} C ${c.from.x + 90 + dx} ${c.from.y}, ${c.to.x - 90 - dx} ${c.to.y}, ${c.to.x - 90} ${c.to.y}`;
            return (
              <path
                key={i}
                d={path}
                stroke={oklch(0.7, 0.06, c.hue, 0.55)}
                strokeWidth="1.4"
                fill="none"
              />
            );
          })}
        </svg>

        {/* Chips */}
        {scopeNodes.map((n) => {
          if (!visibleSet.has(n.id)) return null;
          const px = pixelOf(n.id);
          if (!px) return null;
          const cat = catById[n.catId];
          if (!cat) return null;
          const isFocused =
            focusedNodeIds.has(n.id) || focusedCatIds.has(n.catId);
          // In tree view, we DON'T dim by spotlight when the agent is
          // working — let the focus glow do the highlighting. Dimming
          // hides content the user wants to see during drill-down.
          const dimmed = false;
          const childCount = childCountByParent.get(n.id) ?? 0;
          const isExpanded = expandedNodeIds.has(n.id);
          return (
            <NodeChip
              key={n.id}
              node={n}
              cat={cat}
              x={px.x}
              y={px.y}
              isHovered={hover === n.id || isFocused}
              dimmed={dimmed}
              spotlightFocused={isFocused}
              childCount={childCount}
              expanded={isExpanded}
              onToggleExpand={
                childCount > 0 ? () => toggleExpansion(n.id) : undefined
              }
              onHover={setHover}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── Coverage card (top-right floating) ───────────────────────────────

function CoverageCard({
  sparseCats,
  tensionCount,
  onSwitchToTensions,
}: {
  sparseCats: AtlasCategory[];
  tensionCount: number;
  onSwitchToTensions?: () => void;
}) {
  return (
    <div
      className="absolute z-10"
      style={{
        top: 88,
        right: 22,
        background: "#fff",
        border: "1px solid #e6e4df",
        borderRadius: 12,
        padding: "14px 16px",
        fontFamily: "Inter, sans-serif",
        fontSize: 12,
        color: "#6b665d",
        boxShadow: "0 2px 12px rgba(0,0,0,.05)",
        minWidth: 220,
        maxWidth: 280,
      }}
    >
      <div
        className="text-[10px] font-semibold uppercase"
        style={{ color: "#9a958d", letterSpacing: "0.08em", marginBottom: 10 }}
      >
        Coverage
      </div>

      {sparseCats.length > 0 && (
        <div style={{ marginBottom: tensionCount > 0 ? 14 : 0 }}>
          <div style={{ color: "#1a1815", marginBottom: 6 }}>Sparse sectors</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {sparseCats.map((c) => (
              <span
                key={c.id}
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: oklch(0.97, 0.04, c.hue),
                  color: oklch(0.45, 0.14, c.hue),
                  fontWeight: 500,
                }}
              >
                {c.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {tensionCount > 0 && (
        <div>
          <div style={{ color: "#1a1815" }}>
            <span style={{ color: "#b04020", fontWeight: 600 }}>
              {tensionCount} tension{tensionCount === 1 ? "" : "s"} detected
            </span>
          </div>
          {onSwitchToTensions && (
            <button
              type="button"
              onClick={onSwitchToTensions}
              className="mt-1"
              style={{
                background: "transparent",
                border: "none",
                color: "#6b665d",
                fontSize: 11,
                padding: 0,
                cursor: "pointer",
                textDecoration: "underline",
                textUnderlineOffset: 3,
                fontFamily: "inherit",
              }}
            >
              Switch to Tensions to see
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Depth filter pill ────────────────────────────────────────────────
// Segmented control: "All / 1 / 2 / 3". L1 = direct children of Brand
// (Type-level nodes), L2 = Branches, L3 = leaf anchors. Useful for
// scanning the structural skeleton of the graph vs the leaf detail.

function DepthFilterPill({
  value,
  onChange,
}: {
  value: "all" | 1 | 2 | 3;
  onChange: (next: "all" | 1 | 2 | 3) => void;
}) {
  const items: Array<{
    key: "all" | 1 | 2 | 3;
    label: string;
    hint: string;
  }> = [
    { key: "all", label: "All", hint: "Every node" },
    { key: 1, label: "L1", hint: "Type-level only (depth 1)" },
    { key: 2, label: "L2", hint: "Branches only (depth 2)" },
    { key: 3, label: "L3", hint: "Leaves only (depth 3)" },
  ];
  return (
    <div
      className="inline-flex items-center rounded-full p-0.5 text-[12px] border"
      style={{ background: "#fff", borderColor: "#e6e4df" }}
      title="Filter atlas by tree depth"
    >
      {items.map((it) => {
        const active = value === it.key;
        return (
          <button
            key={String(it.key)}
            type="button"
            onClick={() => onChange(it.key)}
            title={it.hint}
            className="px-2.5 py-1 rounded-full font-medium transition-colors"
            style={{
              background: active ? "#1a1815" : "transparent",
              color: active ? "#fff" : "#6b665d",
              border: "none",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Mode pill ────────────────────────────────────────────────────────

function ModePill({
  mode,
  override,
  onSet,
}: {
  mode: "onboarding" | "living";
  override: "auto" | "onboarding" | "living";
  onSet: (next: "auto" | "onboarding" | "living") => void;
}) {
  const [open, setOpen] = useState(false);
  const isPinned = override !== "auto";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] outline-none transition-colors"
        style={{
          background: "#fff",
          border: "1px solid #e6e4df",
          color: "#3a3733",
          cursor: "pointer",
        }}
        title={
          isPinned
            ? `Pinned to ${override}. Click to change.`
            : `Auto-inferred from node count.`
        }
      >
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{
            background: mode === "onboarding" ? "#3fae6a" : "#9a958d",
            boxShadow:
              mode === "onboarding"
                ? "0 0 0 3px rgba(63,174,106,.15)"
                : "0 0 0 3px rgba(154,149,141,.15)",
          }}
        />
        <span className="capitalize" style={{ color: "#1a1815" }}>
          {mode}
        </span>
        <span style={{ color: "#9a958d", marginLeft: 2 }}>
          · {isPinned ? "pinned" : "auto"}
        </span>
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className="absolute right-0 mt-1.5 w-64 rounded-md z-40 overflow-hidden"
            style={{ background: "#fff", border: "1px solid #e6e4df", boxShadow: "0 12px 32px rgba(0,0,0,.10)" }}
          >
            <div className="px-3 py-2" style={{ borderBottom: "1px solid #e6e4df" }}>
              <div
                className="text-[10px] uppercase"
                style={{ color: "#9a958d", letterSpacing: "0.06em" }}
              >
                Session mode
              </div>
            </div>
            <ModeOption
              label="Auto"
              hint="Infer from node count"
              active={override === "auto"}
              onClick={() => {
                onSet("auto");
                setOpen(false);
              }}
            />
            <ModeOption
              label="Onboarding"
              hint="Greedy batch-extraction"
              active={override === "onboarding"}
              onClick={() => {
                onSet("onboarding");
                setOpen(false);
              }}
            />
            <ModeOption
              label="Living"
              hint="Query first, one change per turn"
              active={override === "living"}
              onClick={() => {
                onSet("living");
                setOpen(false);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function ModeOption({
  label,
  hint,
  active,
  onClick,
}: {
  label: string;
  hint: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 transition-colors flex items-start gap-2"
      style={{
        background: active ? "#f7f6f1" : "transparent",
        border: "none",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "#f4f3ef";
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" style={{ color: "#1a1815" }}>
          {label}
        </div>
        <div className="text-[11px]" style={{ color: "#6b665d" }}>
          {hint}
        </div>
      </div>
      {active && <div style={{ color: "#3fae6a", fontSize: 12 }}>✓</div>}
    </button>
  );
}
